import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ClockIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { apiClient } from '@/services/api'
import { formatDistanceToNow } from 'date-fns'

interface RecentOrchestratorRunsProps {
  /** Click handler — fired with the row's prompt when the user picks a run.
   *  Caller decides what to do: re-prefill the composer, open replay, etc. */
  onPickPrompt?: (prompt: string) => void
  limit?: number
}

/**
 * Recent runs rail for the auto-orchestrator. Per PLAN-orchestrator-ux.md
 * Phase C — surfaces the last N invocations so users can re-prefill the
 * composer or jump to the trace replay in Playground.
 */
export default function RecentOrchestratorRuns({
  onPickPrompt,
  limit = 10,
}: RecentOrchestratorRunsProps) {
  const navigate = useNavigate()
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['orchestrator-recent', limit],
    queryFn: () => apiClient.getRecentOrchestratorRuns(limit),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const items = data ?? []

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Recent runs</h3>
          <span className="text-[11px] text-muted-foreground">auto-orchestrator</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          aria-label="Refresh recent runs"
        >
          <ArrowPathIcon className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-muted/50 rounded animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">
          No recent orchestrator runs. Type a goal in the composer above.
        </p>
      )}

      {!isLoading && items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((it) => {
            const promptPreview = (it.prompt || it.output_summary || '(no prompt captured)').slice(0, 80)
            const ago = it.created_at ? formatDistanceToNow(new Date(it.created_at), { addSuffix: true }) : ''
            const cost =
              it.total_cost_usd != null && it.total_cost_usd > 0
                ? ` · $${it.total_cost_usd.toFixed(3)}`
                : ''
            const dur = it.duration_ms != null ? ` · ${(it.duration_ms / 1000).toFixed(1)}s` : ''
            return (
              <li
                key={it.request_id}
                className="group flex items-start gap-2 rounded-md border border-transparent hover:border-border hover:bg-accent/30 px-2 py-1.5 transition-colors"
              >
                <div className="mt-0.5 flex-shrink-0">
                  {it.success ? (
                    <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <XCircleIcon className="w-3.5 h-3.5 text-red-500" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onPickPrompt?.(it.prompt || '')}
                  className="flex-1 min-w-0 text-left"
                  title={it.prompt || ''}
                >
                  <div className="text-xs text-foreground truncate">{promptPreview}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {ago}
                    {dur}
                    {cost}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/playground?replay=${encodeURIComponent(it.request_id)}`)
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-foreground"
                  aria-label="View trace in Playground"
                  title="View trace in Playground"
                >
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
